import UserAvatar from "@/components/lazo-ai-chat-components/Avatar";
import { Colors } from "@/enums/Colors";
import ChatUserDataDTO from "@/models/chat/ChatUserDataDTO";
import { Box, Button, ListItem, styled, Typography } from "@mui/material";
import React, { Dispatch, SetStateAction, useCallback, useState } from "react";
import { LazoCheckbox } from "../../common/form/LazoCheckbox";
import { StyledInputField } from "../../fundraising/advanced-search/AdvancedSearchFilters";
import { PortalUserAvatarSx } from "./PortalChatAvatar";

type UserListItemProps = {
  key: string;
  user: ChatUserDataDTO;
  onClick: (user: ChatUserDataDTO) => void;
  groupChat: boolean;
  step: number;
  checked: boolean;
};

function arePropsEqual(oldProps: UserListItemProps, newProps: UserListItemProps) {
  for (let key of Object.keys(oldProps)) {
    if (oldProps[key] !== newProps[key]) {
      return false;
    }
  }
  return true;
}

const UserListItem = React.memo((props: UserListItemProps) => {
  return (
    <ItemContainer
      key={props.key}
      loading={false}
      alignItems="flex-start"
      onClick={() => props.onClick(props.user)}
      sx={{ cursor: "pointer" }}
    >
      {props.groupChat && props.step === 0 && (
        <LazoCheckbox
          checked={props.checked}
          sx={{
            padding: 0,
          }}
        />
      )}
      <Box>
        <UserAvatar sx={PortalUserAvatarSx} user={props.user} />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Typography fontWeight="600">{props.user.firstName + " " + props.user.lastName}</Typography>
        <Typography fontSize="0.75rem" fontWeight="500" sx={{ color: Colors.DARKER_DARKER_GRAY }}>
          {props.user.email}
        </Typography>
        <Typography>{props.user.tenantNames.join(", ")}</Typography>
      </Box>
    </ItemContainer>
  );
}, arePropsEqual);

type Props = {
  onUserSelect: (user: ChatUserDataDTO) => void;
  step: number;
  setStep: (step: number) => void;
  checkedUsers: Record<string, ChatUserDataDTO>;
  setCheckedUsers: Dispatch<SetStateAction<Record<string, ChatUserDataDTO>>>;
  groupChat?: boolean;
  inboxUserChatData?: ChatUserDataDTO[];
  onCreateGroupChat: (users: string[], name: string) => void;
  loading?: boolean;
};

export const PortalUserItemComponent = ({
  onUserSelect,
  onCreateGroupChat,
  step,
  setStep,
  groupChat,
  inboxUserChatData,
  checkedUsers,
  setCheckedUsers,
}: Props) => {
  const [groupName, setGroupName] = useState("");

  function handleUserSelect(user: ChatUserDataDTO) {
    setCheckedUsers((prevCheckedUsers) => {
      const newCheckedUsers: Record<string, ChatUserDataDTO> = { ...prevCheckedUsers };
      if (newCheckedUsers[user.externalId!]) {
        delete newCheckedUsers[user.externalId!];
      } else {
        newCheckedUsers[user.externalId!] = user;
      }
      return newCheckedUsers;
    });
  }

  function generateGroupName(users: ChatUserDataDTO[]) {
    let gname = "";
    for (let u of users) {
      if (!u || u.tenantExternalIds[0] === "lazoTenant0") {
        continue;
      }

      gname += u.tenantNames[0] + ", ";
    }

    if (gname.length !== 0) {
      gname = gname.slice(0, gname.length - 2);
    }

    setGroupName(gname);
  }

  function createGroupChat() {
    onCreateGroupChat(Object.keys(checkedUsers), groupName);
  }

  function onClickNext() {
    generateGroupName(Object.values(checkedUsers));
    setStep(1);
    getSelectedUsersToChatGroup();
  }

  function getSelectedUsersToChatGroup(): ChatUserDataDTO[] {
    return Object.keys(checkedUsers).map((k) => checkedUsers[k]);
  }

  const handleChatListItemClick = useCallback(
    (user: any) => {
      if (!groupChat) {
        onUserSelect(user);
      } else if (groupChat && step === 0) {
        handleUserSelect(user);
      }
    },
    [inboxUserChatData]
  );

  const arrayToMapInbox = step !== 1 ? inboxUserChatData : getSelectedUsersToChatGroup();

  return (
    <Box display="flex" width="100%" height="100%" flexDirection="column" gap="0.5rem">
      <Box sx={{ height: "90%", overflowY: "auto" }}>
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                padding: "0.5rem",
                gap: "0.25rem",
              }}
            >
              <Typography
                sx={{
                  fontWeight: "300",
                  color: Colors.LIGHT_GRAY_8E8EA0,
                  fontSize: "0.75rem",
                }}
              >
                Group Name
              </Typography>
              <StyledInputField
                required
                onChange={(e) => setGroupName(e.target.value)}
                hideIcon
                placeholder="Enter group name..."
                value={groupName}
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  ".MuiOutlinedInput-root": {
                    padding: "0 0.5rem",
                  },
                  ".MuiOutlinedInput-input": {
                    padding: 0,
                  },
                }}
              />
            </Box>

            <Typography
              sx={{
                fontWeight: "500",
                borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
                padding: "0 0.5rem 0.5rem 0.5rem",
              }}
            >
              Members selected
            </Typography>
          </Box>
        )}

        {arrayToMapInbox.length === 0 && (
          <Box display="flex" alignItems="center" padding="0.5rem" justifyContent="center">
            <Typography fontSize="0.875rem">No results</Typography>
          </Box>
        )}

        {arrayToMapInbox.map((user) => (
          <UserListItem
            key={user.externalId}
            user={user}
            checked={!!checkedUsers[user.externalId!]}
            onClick={handleChatListItemClick}
            groupChat={groupChat}
            step={step}
          />
        ))}
      </Box>
      <Box display="flex" alignItems="center" justifyContent="center">
        {groupChat ? (
          step !== 1 ? (
            <SubmitButton disabled={Object.keys(checkedUsers).length <= 1} variant="contained" onClick={onClickNext}>
              <Typography>Next</Typography>
            </SubmitButton>
          ) : (
            <SubmitButton variant="contained" onClick={createGroupChat}>
              <Typography>Submit</Typography>
            </SubmitButton>
          )
        ) : null}
      </Box>
    </Box>
  );
};

const SubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: Colors.PURPLE,
  color: "#FFFFFF",
  width: "90%",
  height: "2.75rem",
  borderRadius: theme.shape.borderRadius,
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: Colors.LAZO_PURPLE2,
  },
}));

const ItemContainer = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "loading",
})<{ loading: boolean }>(({ theme, loading }) => ({
  display: "flex",
  alignItems: "center",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  padding: "0.25rem 0.75rem",
  margin: "0",
  gap: "0.875rem",
  width: "100%",
  minWidth: "16.938rem",
  height: "4.5rem",
  transition: theme.transitions.create(["background-color", "box-shadow"], {
    duration: theme.transitions.duration.short,
  }),

  ":nth-child(1)": {
    borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },

  "&:hover": {
    backgroundColor: Colors.BUTTON_SECONDARY_PURPLE,
  },

  ...(loading && {
    backgroundColor: Colors.LIGHT_GRAY,
    cursor: "default",
    "&:hover": {},
  }),
}));

export default PortalUserItemComponent;
